import './Input.css';

import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import Icon from './Icons/Icon';
import colours from '../Colours';

const Input = React.forwardRef((props, ref) => {

    let {
        leading_ic,
        leading_ic_onClick,
        leading_ic_style,
        required,
        dropdown,
        textarea,
        variant,
        message,
        inputclass,
        dropdownContainerProps,
        dispatch,
        userSession,
        ...otherProps
    } = props;

    const containerRef = React.useRef();
    const [dropdownOpen, setDropdownOpen] = useState(false);
    
    useEffect(()=> {
        if (dropdown)
            document.addEventListener('mousedown', handleMouseDown);
        

        return (()=> {
            if (dropdown)
                document.removeEventListener('mousedown', handleMouseDown);
        });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dropdown])

    const getVariant = () => {
        if (variant === 'info' || variant === 'error' || variant === 'warning' || variant === 'success')
            return variant;
        
        else return '';
    }

    const getColor = (variant) => {
        if (variant === 'info')
            return colours.primary;
        
        if (variant === 'success')
            return colours.validate_green;
        
        if (variant === 'error')
            return colours.error_red;
        
        return colours.grey03;
    }

    const getIcon = (variant) => {
        if (variant === 'info' || variant === 'error' || variant === 'warning')
            return "AttentionForm";
        
        if (variant === 'success')
            return "SuccessForm"
        
        return '';
    }

    const handleMouseDown = (event) => {

        if (dropdown && containerRef.current && !containerRef.current.contains(event.target)) {
            setDropdownOpen(false);
        }
    }

    return (
        <>
            <div className="ds-input-outer-container" ref={containerRef}>
                <div className="ds-input-label">
                    <div className="ds-input-label-text">{props.label}</div>
                    {required ? <div className="ds-input-required">*</div> : null}
                </div>
                <div className="ds-input-inner-container" ref={dropdown ? ref : null} onClick={dropdown && !props.disabled ? ()=> {
                            setDropdownOpen(current => !current);
                        } : null}>
                    {textarea ?
                        null
                        : <div className={"ds-input-leading_ic" + (leading_ic_onClick && (!props.disabled) ? " onclick" : "" ) + (props.readOnly ? " readonly" : "")} onClick={leading_ic_onClick && (!props.disabled) ? leading_ic_onClick : null}>
                            <Icon name={leading_ic ? leading_ic : null} size={20} style={leading_ic_style ? leading_ic_style : (props.disabled || props.readOnly) ? {color: colours.primary.grey02} : {color: colours.primary.grey01}}/>
                        </div> 
                    }
                    {dropdown ? 
                        <div 
                            tabIndex={0}
                            className={"ds-input dropdown " 
                                + getVariant()
                                + (leading_ic ? " pl-36" : "") 
                                + (props.disabled ? " disabled" : "") 
                                + " " + inputclass}>
                            {props.value && props.value !== '' ? 
                                props.value
                                : <div className="ds-placeholder">{props.placeholder}</div>
                            }
                        </div>
                    :   textarea ? 
                        <textarea
                            className={"ds-textarea ds-input " + getVariant() + " " + inputclass}
                            tabIndex={0}
                            disabled={props.disabled}
                            name={props.name}
                            value={props.value}
                            readOnly={props.readOnly}
                            ref={ref}
                            onChange={props.onChange}
                            placeholder={props.placeholder ? props.placeholder : null}
                            {...otherProps}
                        />
                    :  <input
                            type={"text"}
                            tabIndex={0}
                            className={"ds-input " + getVariant() + (leading_ic ? " pl-36" : "") + " " + inputclass}
                            disabled={props.disabled}
                            name={props.name}
                            value={props.value}
                            readOnly={dropdown || props.readOnly}
                            ref={ref}
                            onChange={props.onChange}
                            placeholder={props.placeholder ? props.placeholder : null}
                            {...otherProps}
                        /> 
                    }
                    {dropdown ? 
                        <div className="ds-input-dropdown_ic">
                            <Icon name={dropdownOpen ? "ChevronUp" : "ChevronDown"} size={16} style={(props.disabled || props.readOnly) ? {color: colours.grey03} : {color: colours.grey01}}/>
                        </div> 
                    : null}
                </div>
                {dropdownOpen ? 
                    <div className="ds-input-dropdown-container" {...dropdownContainerProps}>
                        {props.children.map((child) => {
                            if ((props.value === child.props.value) || (props.value === child.props.label)) {
                                return (
                                    <React.Fragment key={child.props.value ? child.props.value : child.props.label}> 
                                        {React.cloneElement(child, { active: true, onChange: props.onChange, setDropdownOpen })} 
                                    </React.Fragment>
                                )
                            } else {
                                return (
                                    <React.Fragment key={child.props.value ? child.props.value : child.props.label}> 
                                        {React.cloneElement(child, { active: false, onChange: props.onChange, setDropdownOpen })}
                                    </React.Fragment>
                                )
                            }
                        })}
                    </div>
                : null}
            </div>
            {(getVariant() !== '') && message ? 
                <div className={"ds-input-message " + getVariant()}>
                    <div className="ds-input-message-icon">
                        <Icon name={getIcon(getVariant())} size={16} style={{color: getColor(getVariant())}}/>
                    </div>
                    <div className="ds-input-message-text">
                        {message}    
                    </div>
                </div>
            : null}
        </>
    )
});

Input.defaultProps = {
    label: '',
    required: false,
    leading_ic: '',
    leading_ic_onClick: null,
    leading_ic_style: null,
    placeholder: '',
    name: '',
    onChange: null,
    value: '',
    dropdown: false,
    textarea: false,
    disabled: false,
    readOnly: false,
    variant: '',
    message: '',
    inputclass: '',
    dropdownContainerProps: null
};

Input.propTypes = {
    label: PropTypes.string.isRequired,
    required: PropTypes.bool,
    leading_ic: PropTypes.string,
    leading_ic_onClick: PropTypes.func,
    leading_ic_style: PropTypes.object,
    placeholder: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func,
    value: PropTypes.string,
    dropdown: PropTypes.bool,
    textarea: PropTypes.bool,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    variant: PropTypes.oneOf(['info', 'error', 'warning', 'success', '']),
    message: PropTypes.string,
    inputclass: PropTypes.string,
    dropdownContainerProps: PropTypes.object
}

const DropdownItem = (props) => {
    return (
        <div
            className={"ds-dropdown-item-container" + (props.active ? " active" : "")}
            key={props.value ? props.value : props.label} 
            data-id={props.value ? props.value : props.label}
            onClick={(e) => {props.onChange(e); props.setDropdownOpen(false)}}
        >
            <div style={{display: 'flex', flex: 1, justifySelf: 'flex-start', flexDirection: 'row', alignItems: 'center'}}>
                <div className="ds-dropdown-item-label">{props.label}</div>
            </div>
            {props.active ? 
                <div style={{display: 'flex', justifySelf: 'flex-end', flexDirection: 'row', alignItems: 'center', marginRight: 4}}>
                    <Icon name="Check" size={16}/>
                </div>
            : null}
        </div>
    )
}

DropdownItem.defaultProps = {
    active: false,
    label: '',
}

DropdownItem.propTypes = {
    active: PropTypes.bool,
    label: PropTypes.string.isRequired,
}

export { Input, DropdownItem };